import React, { useState, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import moment from "moment";
import { DateRangePicker } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";

import { User } from "@/types" 

import { usersGetAmountReport } from "@/store/users";

import AppBox from "@/components/AppBox";
import AppButton from "@/components/AppButton";
import AppLoader from "@/components/AppLoader";
import AppText from "@/components/AppText";

interface ReportModalProps {
  user?: User;
  onClose: () => void;
}

const ReportModal: React.FC<ReportModalProps> = ({ user, onClose }) => {
  const dispatch = useAppDispatch();
  const today = moment();

  const { amountReport, reportStatus } = useAppSelector((state) => state.users);

  const [dateValue, setDateValue] = useState([
    {
      startDate: today.toDate(),
      endDate: today.toDate(),
      key: "selection",
    },
  ]);

  useEffect(() => {
    if (user) {
      dispatch(usersGetAmountReport({ id: user.id, date: dateValue } ));
    }
  }, [user, dateValue, dispatch]);

  const handleChangeDate = async (newValue: any) => {
    const updatedDateValue = newValue.map((range: any) => ({
      ...range,
      startDate: moment(range.startDate).format("YYYY-MM-DD"),
      endDate: moment(range.endDate).format("YYYY-MM-DD"),
    }));

    setDateValue(updatedDateValue);
    if (user) {
      dispatch(usersGetAmountReport({ id: user.id, date: updatedDateValue }));
    }
  };

  return (
    <AppBox column gap={4} className="p-6 bg-white">
      {/* Modal Title */}
      <AppBox column align="center" gap={1}>
        <AppText variant="h5" className="text-center font-bold">
          {user?.name}
        </AppText>
        <AppText className="text-center text-gray-500">{user?.email}</AppText>
      </AppBox>

      {/* Date Picker */}
      <DateRangePicker
        onChange={(item: any) => handleChangeDate([item.selection])}
        // showSelectionPreview={true}
        moveRangeOnFirstSelection={false}
        months={1}
        ranges={dateValue}
        direction="horizontal"
        inputRanges={[]}
      />

      {/* Report Content */}
      <AppBox column align="center" gap={4} className="w-full">
        <AppText variant="h6" className="text-center font-bold">
          Report for{" "}
          {dateValue[0].startDate === dateValue[0].endDate
            ? moment(dateValue[0].startDate).format("Do [of] MMMM")
            : `${moment(dateValue[0].startDate).format("Do [of] MMMM")} to ${moment(
                dateValue[0].endDate
              ).format("Do [of] MMMM")}`}
        </AppText>

        <AppBox justify="center" gap={8} className="w-full">
          <AppBox column align="center" gap={1}>
            <AppText className="uppercase font-bold">Autoresponders</AppText>
            <AppText className="text-xl font-bold">
              {reportStatus === "loading" ? <AppLoader size={24} /> : amountReport?.gls_count || 0}
            </AppText>
          </AppBox>

          <AppBox column align="center" gap={1}>
            <AppText className="uppercase font-bold">Texts</AppText>
            <AppText className="text-xl font-bold">
              {reportStatus === "loading" ? <AppLoader size={24} /> : amountReport?.texts_count || 0}
            </AppText>
          </AppBox>

          <AppBox column align="center" gap={1}>
            <AppText className="uppercase font-bold">AI generations</AppText>
            <AppText className="text-xl font-bold">
              {reportStatus === "loading" ? <AppLoader size={24} /> : amountReport?.ai_generations_count || 0}
            </AppText>
          </AppBox>
        </AppBox>
      </AppBox>

      {/* Close Button */}
      <AppBox justify="center" className="mt-4">
        <AppButton onClick={onClose}>Close</AppButton>
      </AppBox>
    </AppBox>
  );
};

export default ReportModal;